import type {
    FetchOptions,
    FetchRequest,
    FetchResponse,
    IFetchError,
} from "ofetch";

export interface ApiErrorResponse {
    message: string;
    errors?: Record<string, string[]>;
}

function isApiErrorResponse(value: unknown): value is ApiErrorResponse {
    return (
        typeof value === "object" &&
        value !== null &&
        "message" in value &&
        typeof (value as ApiErrorResponse).message === "string"
    );
}

export class ApiError<T = unknown> extends Error {
    public override message: string;
    public errors: Record<keyof T | string, string[]> | undefined;
    public request?: FetchRequest;
    public options?: FetchOptions;
    public response?: FetchResponse<T>;

    constructor(
        error: ApiErrorResponse | IFetchError,
        response?: FetchResponse<T>,
    ) {
        super(
            isApiErrorResponse(error)
                ? error.message
                : (error as IFetchError)?.message ||
                      "An unexpected error occurred.",
        );

        if ((error as IFetchError)?.request) {
            this.request = (error as IFetchError).request;
        }

        if ((error as IFetchError)?.options) {
            this.options = (error as IFetchError).options;
        }

        let data: ApiErrorResponse | undefined;
        if ((error as IFetchError)?.response) {
            this.response = (error as IFetchError).response;
            const fetchError = error as IFetchError<ApiErrorResponse>;
            const responseData = fetchError.response?._data;

            if (isApiErrorResponse(fetchError.data)) {
                data = fetchError.data;
            } else if (isApiErrorResponse(responseData)) {
                data = responseData;
            }
        } else {
            data = isApiErrorResponse(error) ? error : undefined;
            this.response = response;
        }

        this.message =
            data?.message ||
            (error as IFetchError)?.message ||
            this.response?.statusText ||
            "An unexpected error occurred.";
        this.errors = data?.errors as
            | Record<keyof T | string, string[]>
            | undefined;
    }

    has(key: keyof T | string) {
        return this.errors && this.errors[key];
    }

    get(key: keyof T | string): string[] | undefined {
        return this.errors ? this.errors[key] : undefined;
    }
}
